import {
	GET_RESERVATION_CONTEXT,
	LOAD_CARDS,
	ADD_CARD,
	SELECT_PAYMENT_METHOD,
	SELECT_CARD,
	RESERVATION_RESULT,
	ADD_PARTNER_TO_RESERVATION,
	REMOVE_PARTNER_FROM_RESERVATION,
	CLEAR_RESERVATION_CONTEXT,
	RESERVATION_CONTEXT_PRELOAD,
	SHOW_UNAVAILABILITY,
	LOAD_CLUB,
	ADD_RESERVATION_ERROR,
	EDIT_RESERVATION,
	RESERVATION_VALIDATE,
	CLOSE_VALIDATE_PROFILE_POPUP,
	makeBooking,
	SET_FEDERATION_USERID,
	SELECT_NETWORK,
} from "../actions";
import {
	ReservationContext,
	Card,
	Reservation,
	User,
	Court,
	Slot,
	Championship,
	PaymentMethod,
	ProfileValidationFlag,
} from "../types";
import { call } from "ramda";
import cards from "./cards";

export type ReservationState = typeof initialState;

const emptyParams = {
	courtId: null as null | string,
	duration: 0,
	startDateTime: null as null | number,
	ballMachine: false,
	numberOfGuests: 0,
	voucherCode: null as null | string,
	paymentMethod: null as null | string,
	partnerIds: null as null | string,
	championshipId: null as null | string,
	paymentIntent: null as null | string,
};

export const initialState = {
	context: null as null | ReservationContext,
	params: emptyParams,
	cards: [] as Card[],
	selectedCard: null,
	selectedNetwork: null,
	networks: [],
	result: null as null | Reservation,
	playerSuggestions: [],
	partners: [] as User[],
	court: null as Court,
	slots: [],
	unavailableSlot: null as Slot,
	selectedReservation: null as Reservation,
	error: null as string,
	edit: false,
	showCompleteProfile: false,
	bookingValidated: false,
	callback: null,
	federationUserId: null as string,
};

function getNumberOfGuests(partners?: Array<User>): number {
	if (!partners) {
		return 0;
	}

	if (partners.length === 0) {
		return 0;
	}

	return partners.filter((p) => p.type === "guest").length;
}

function getSelectedCard(cards: Card[], id?: string): Card | null {
	if (!id) return null;
	for (let card of cards) {
		if (card.cardId === id) {
			return card;
		}
	}
	return null;
}

function getNetworks(card: Card) {
	let networks = card?.networks?.available || [];
	let selectedNetwork = card?.networks?.preferred || null;
	if (!selectedNetwork) {
		selectedNetwork = networks.length >= 1 ? networks[0] : null;
	}
	return { networks, selectedNetwork };
}

export default function (state: ReservationState = initialState, action) {
	switch (action.type) {
		case RESERVATION_CONTEXT_PRELOAD: {
			if (action.reservation.params.duration === 0) {
				return initialState;
			}

			const { params, court } = action.reservation;

			const slot = action.slot;
			let slots = state.slots;

			if (slot) {
				slots = [slot];
			}

			return { ...state, params, court, slots, unavailableSlot: null, result: null, error: null };
		}
		case GET_RESERVATION_CONTEXT: {
			if (action.reservation.params.duration === 0) {
				return initialState;
			}

			const { context, params: parameters } = action.reservation;

			return {
				...state,
				context,
				params: { ...state.params, ...parameters },
				unavailableSlot: null,
				result: null,
				error: null,
			};
		}

		case SHOW_UNAVAILABILITY: {
			return { ...initialState, court: action.court, unavailableSlot: action.slot, result: null };
		}

		case "@@router/LOCATION_CHANGE":
		case LOAD_CLUB:
		case CLEAR_RESERVATION_CONTEXT: {
			if (action.noReset) {
				return state;
			}

			const { cards, federationUserId } = state;
			return { ...initialState, cards, federationUserId };
		}

		case LOAD_CARDS: {
			const { cards } = action;
			return { ...state, cards };
		}

		case ADD_CARD: {
			const { card } = action;
			let { cards } = state;

			cards = cards.concat(card);

			const paymentMethod = "creditcard";
			const selectedCard = card.cardId;
			return { ...state, cards, selectedCard, params: { ...state.params, paymentMethod } };
		}

		case SELECT_PAYMENT_METHOD: {
			const { paymentMethod } = action;
			return { ...state, params: { ...state.params, paymentMethod } };
		}

		case SELECT_CARD: {
			const { selectedCard } = action;
			const paymentMethod = "creditcard";
			const card = getSelectedCard(state.cards, selectedCard);
			const { networks, selectedNetwork } = getNetworks(card);
			return { ...state, selectedCard, networks, selectedNetwork, params: { ...state.params, paymentMethod } };
		}

		case SELECT_NETWORK: {
			const { selectedNetwork } = action;
			return { ...state, selectedNetwork };
		}

		case RESERVATION_RESULT: {
			const { reservation } = action;
			return { ...state, params: emptyParams, result: reservation };
		}

		case RESERVATION_VALIDATE: {
			const { context, callback } = action;
			if (
				Array.isArray(context.requiredValidation) &&
				context.requiredValidation.includes(ProfileValidationFlag.ContactDetails)
			) {
				return { ...state, showCompleteProfile: true, bookingValidated: false, callback: callback };
			} else {
				return { ...state, showCompleteProfile: false, bookingValidated: true, callback: null };
			}
		}

		case CLOSE_VALIDATE_PROFILE_POPUP: {
			return { ...state, showCompleteProfile: false, callback: null };
		}

		case ADD_PARTNER_TO_RESERVATION: {
			const { user, championshipId } = action;
			let { partners } = state;
			let numberOfGuests = 0;

			for (let partner of partners) {
				if (partner.hasOwnProperty("userId") && user.hasOwnProperty("userId")) {
					if (partner.userId === user.userId) {
						return state;
					}
				}
			}

			partners = partners.concat([user]);
			let partnerIds = [];
			partners.forEach((user) => {
				if (["player", "guest"].includes(user.type)) {
					partnerIds.push(user.playerId);
				}
			});

			numberOfGuests = getNumberOfGuests(partners);

			return {
				...state,
				partners,
				params: {
					...state.params,
					numberOfGuests,
					partnerIds: partnerIds.join(","),
					championshipId: championshipId,
				},
			};
		}

		case REMOVE_PARTNER_FROM_RESERVATION: {
			const { index } = action;
			let { partners } = state;

			let numberOfGuests = 0;

			partners.splice(index, 1);

			const partnerIds = partners.map((user) => {
				return user.userId;
			});

			numberOfGuests = getNumberOfGuests(partners);

			return {
				...state,
				partners: [...partners],
				params: { ...state.params, numberOfGuests, partnerIds: partnerIds.join(",") },
			};
		}

		case ADD_RESERVATION_ERROR: {
			const { errorCode, errorText } = action;

			return { ...state, error: errorText };
		}

		case SET_FEDERATION_USERID: {
			const { federationUserId } = action;
			return { ...state, federationUserId };
		}

		case EDIT_RESERVATION: {
			const reservation = action.reservation as Reservation;
			const { court, championship }: { court: Court; championship: Championship } = reservation;

			let numberOfGuests = 0;
			let partnerIds = [];

			const partners = reservation.players
				.filter((player) => {
					return player.playerId !== reservation.ownerId;
				})
				.map((player) => {
					return { ...player, userId: player.playerId };
				});

			partners.forEach((user) => {
				if (["player", "guest"].includes(user.type)) {
					partnerIds.push(user.playerId);
				}

				if (user.type === "guest") {
					numberOfGuests++;
				}
			});

			let selectedCard,
				networks,
				selectedNetwork = null;
			const { paymentMethod, voucherCode, paymentCardInfo } = reservation.paymentDetails;
			if (paymentMethod === PaymentMethod.CreditCard) {
				selectedCard = paymentCardInfo.cardId;
				const card = getSelectedCard(state.cards, selectedCard);
				({ networks, selectedNetwork } = getNetworks(card));
			}
			let championshipId = null;

			if (championship) {
				championshipId = championship.uuid;
			}

			const duration = (reservation.endDateTime - reservation.startDateTime) / 60;

			const params = {
				courtId: court.courtId,
				duration,
				startDateTime: reservation.startDateTime,
				ballMachine: !!parseInt(reservation.ballMachine),
				numberOfGuests,
				voucherCode,
				paymentMethod,
				partnerIds,
				championshipId,
			};

			return {
				...state,
				params,
				partners,
				selectedReservation: reservation,
				edit: true,
				court,
				selectedCard,
				networks,
				selectedNetwork,
			};
		}

		default:
			return state;
	}
}
